/**
 * PlanningBoardPage (epic-6 §3b / PRJ-4) — route /projects/:id/board.
 *
 * 5 status columns (pending | in_progress | blocked | completed | cancelled)
 * plus any custom `boardColumn` columns (WS-D / PRJ-4).
 * Task cards show title, depth badge and assignee (ownerId). Moving a task is
 * a dropdown (PUT status) — drag & drop intentionally deferred to v2. Each
 * column has a "+ task" button that creates a task with that status (or in
 * that custom column) + project.
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/shared/Toast';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useGetPlanningBoardQuery, useGetTaskQuery, useUpdateTaskMutation, useCreateTaskMutation } from '@/store/api/apiSlice';
import { useAppSelector } from '@/store/hooks';
import { useGetUsersQuery } from '@/store/api/apiSlice';
import {
  BOARD_STATUSES,
  STATUS_LABELS,
  splitBoard,
  depthBadge,
} from './planningBoard';
import type { Task, TaskStatus } from '@/types';

const COLUMN_COLORS: Record<TaskStatus, string> = {
  pending: 'border-slate-300',
  in_progress: 'border-sky-400',
  blocked: 'border-amber-400',
  completed: 'border-emerald-400',
  cancelled: 'border-rose-300',
};

export function PlanningBoardPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const currentUserId = useAppSelector((s) => s.auth.user?._id ?? (s.auth.user as any)?.id);

  const { data: board, isLoading, refetch } = useGetPlanningBoardQuery({ projectId });
  const [updateTask, { isLoading: isMoving }] = useUpdateTaskMutation();
  const [createTask] = useCreateTaskMutation();
  const { data: users } = useGetUsersQuery({ page: 1, pageSize: 100 });

  const [createColumn, setCreateColumn] = useState<string | null>(null);
  const [createTitle, setCreateTitle] = useState('');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const { data: taskDetail } = useGetTaskQuery(selectedTask?._id ?? '', { skip: !selectedTask });

  const userList =
    ((users as { data?: Array<{ _id: string; username: string }> } | undefined)?.data ?? []) as Array<{
      _id: string;
      username: string;
    }>;
  const userOf = (id: string | undefined) => userList.find((u) => u._id === id)?.username ?? id ?? '—';

  // The board endpoint returns Record<string, Task[]> — the 5 status columns
  // plus any custom boardColumn columns (WS-D / PRJ-4).
  const grouped = board && typeof board === 'object' ? (board as Record<string, Task[]>) : undefined;
  const allTasks = grouped ? Object.values(grouped).flat() : [];
  const split = splitBoard(allTasks);
  const columnDefs: Array<{ key: string; label: string; color: string; tasks: Task[]; isCustom: boolean }> = [
    ...BOARD_STATUSES.map((status) => ({
      key: status as string,
      label: STATUS_LABELS[status],
      color: COLUMN_COLORS[status],
      tasks: split.byStatus[status],
      isCustom: false,
    })),
    ...Object.keys(split.custom).map((name) => ({
      key: name,
      label: name,
      color: 'border-violet-400',
      tasks: split.custom[name],
      isCustom: true,
    })),
  ];

  const moveTask = async (task: Task, status: TaskStatus) => {
    if (task.status === status && !task.boardColumn) return;
    try {
      // Moving via the status dropdown returns the task to its status column:
      // clear any custom boardColumn (WS-D / PRJ-4).
      const body = task.boardColumn ? { status, boardColumn: '' } : { status };
      await updateTask({ id: task._id, body }).unwrap();
      toast.success(`Moved "${task.title}" to ${STATUS_LABELS[status]}`);
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to move task');
    }
  };

  const createInColumn = async () => {
    if (!createColumn || !createTitle.trim()) {
      toast.error('Title is required');
      return;
    }
    const isStatusColumn = (BOARD_STATUSES as string[]).includes(createColumn);
    try {
      await createTask({
        ownerId: currentUserId ?? 'admin',
        title: createTitle.trim(),
        status: isStatusColumn ? (createColumn as TaskStatus) : 'pending',
        ...(isStatusColumn ? {} : { boardColumn: createColumn }),
        projectId,
      }).unwrap();
      setCreateColumn(null);
      setCreateTitle('');
      toast.success('Task created');
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to create task');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(projectId ? `/projects/${projectId}` : '/projects')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Planning Board</h1>
          <p className="text-sm text-muted-foreground">
            {projectId ? `Project board` : 'All tasks'} — move tasks via the status dropdown
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>Refresh</Button>
      </div>

      {isLoading && !board ? (
        <LoadingSpinner className="py-16" />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
          {columnDefs.map((col) => (
            <div key={col.key} className={`flex min-h-[240px] flex-col rounded-lg border bg-card/60 ${col.color}`}>
              <div className="flex items-center justify-between border-b px-3 py-2">
                <span className="text-sm font-semibold">{col.label}</span>
                <Badge variant="outline" className="text-xs">{col.tasks.length}</Badge>
              </div>
              <div className="flex-1 space-y-2 p-2">
                {col.tasks.length === 0 && (
                  <p className="py-4 text-center text-xs text-muted-foreground">No tasks</p>
                )}
                {col.tasks.map((task) => (
                  <div key={task._id} className="rounded-md border bg-card p-2 shadow-sm">
                    <button
                      type="button"
                      className="w-full text-left text-sm font-medium hover:text-primary"
                      onClick={() => setSelectedTask(task)}
                    >
                      {task.title}
                    </button>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1">
                      {task.depth > 0 && (
                        <span className="font-mono text-[10px] text-muted-foreground" title={`depth ${task.depth}`}>
                          {depthBadge(task.depth)}
                        </span>
                      )}
                      <Badge variant="secondary" className="text-[10px]">
                        {userOf(task.ownerId)}
                      </Badge>
                    </div>
                    <div className="mt-2">
                      <select
                        className="h-7 w-full rounded border border-input bg-background px-1 text-xs"
                        value={task.status}
                        disabled={isMoving}
                        onChange={(e) => moveTask(task, e.target.value as TaskStatus)}
                      >
                        {BOARD_STATUSES.map((s) => (
                          <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-dashed text-xs"
                  onClick={() => {
                    setCreateColumn(col.key);
                    setCreateTitle('');
                  }}
                >
                  <Plus className="mr-1 h-3 w-3" /> Task
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create task dialog */}
      <Dialog open={!!createColumn} onOpenChange={(open) => !open && setCreateColumn(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New task — {createColumn ? (STATUS_LABELS[createColumn as TaskStatus] ?? createColumn) : ''}</DialogTitle>
            <DialogDescription>Creates a task in this project's planning board.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              autoFocus
              value={createTitle}
              onChange={(e) => setCreateTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createInColumn()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateColumn(null)}>Cancel</Button>
            <Button onClick={createInColumn}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task detail dialog */}
      <Dialog open={!!selectedTask} onOpenChange={(open) => !open && setSelectedTask(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedTask?.title}</DialogTitle>
            <DialogDescription>
              {taskDetail?.description || 'No description'}
            </DialogDescription>
          </DialogHeader>
          {taskDetail && (
            <div className="space-y-2 text-sm">
              <div className="flex gap-2">
                <Badge variant="outline" className="text-xs">{STATUS_LABELS[taskDetail.status]}</Badge>
                <Badge variant="secondary" className="text-xs">owner: {userOf(taskDetail.ownerId)}</Badge>
                {taskDetail.depth > 0 && <Badge variant="outline" className="text-xs">depth {taskDetail.depth}</Badge>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}